import React from 'react'
import { Select as AntSelect, Tooltip } from 'antd'
import { Select } from '@surecompanies/core_components'
import {
  CheckOutlined,
  DeleteOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import isNil from 'lodash/isNil'
import { EMAIL_DELIMITER_REGEX } from '../helpers'
import { sureco } from '../shared/finance.styles'
import {
  FieldErrorMessage,
  InvalidDateInput,
  PlanPill,
} from './ClientBillingProfileManagement.styles'
import {
  isInvalidPaymentType,
  UPLOAD_PAYMENT_TYPE_LABELS,
  type ParsedRow,
} from './bulkUpload.helpers'
import type { RowPlan } from './bulkUploadPlan'

/** The preview grid's cells. Each one edits the parsed row in place. */

export interface UploadClientOption {
  id: string
  name: string
}

const PLAN_LABELS: Record<RowPlan['kind'], string> = {
  create: 'New',
  update: 'Update',
  unchanged: 'No change',
  needsEmails: 'Needs emails',
}

const PLAN_HINTS: Record<RowPlan['kind'], string> = {
  create: 'This company has no profile yet, so one is created',
  update: '',
  unchanged: 'Every filled-in cell already matches the stored profile',
  needsEmails: 'A new profile cannot be created without To Emails',
}

const EmptyCell = () => <span style={{ color: sureco.fg3 }}>—</span>

const ChangeCell = ({ plan }: { plan: RowPlan | undefined }) => {
  // Absent while the stored profiles load, and for rows that cannot be resolved
  // until their client name is fixed.
  if (isNil(plan)) return <EmptyCell />

  const hint =
    plan.kind === 'update'
      ? `Changes ${plan.changedFields.join(', ')}`
      : PLAN_HINTS[plan.kind]

  return (
    <Tooltip title={hint}>
      <PlanPill $variant={plan.kind}>{PLAN_LABELS[plan.kind]}</PlanPill>
    </Tooltip>
  )
}

export const PlanCell = ({
  row,
  plan,
}: {
  row: ParsedRow
  plan: RowPlan | undefined
}) => (row.clientMatch === false ? <EmptyCell /> : <ChangeCell plan={plan} />)

const splitEmails = (value: string): string[] =>
  value === ''
    ? []
    : value
        .split(EMAIL_DELIMITER_REGEX)
        .map((email) => email.trim())
        .filter(Boolean)

const filterClientOption = (
  input: string,
  option: { children?: string } | undefined
) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase())

export const ClientNameCell = ({
  name,
  row,
  clients,
  onClientFix,
}: {
  name: string
  row: ParsedRow
  clients: UploadClientOption[]
  onClientFix: (rowNum: number, clientName: string) => void
}) => {
  const isUnmatched = row.clientMatch === false
  return (
    <div>
      {isUnmatched ? (
        <FieldErrorMessage>
          <WarningOutlined />
          {name === '' ? 'Client Name is required' : `"${name}" not found`}
        </FieldErrorMessage>
      ) : null}
      <Select
        showSearch
        size="small"
        status={isUnmatched ? 'error' : undefined}
        style={{ width: '100%' }}
        value={isUnmatched ? undefined : name}
        placeholder={isUnmatched ? 'Select correct client' : 'Change client…'}
        optionFilterProp="children"
        filterOption={filterClientOption}
        onChange={(value: string) => {
          const client = clients.find((c) => c.id === value)
          if (client) onClientFix(row.rowNum, client.name)
        }}
        suffixIcon={
          isUnmatched ? undefined : (
            <CheckOutlined
              style={{ color: sureco.successText, fontSize: 11 }}
            />
          )
        }
      >
        {clients.map((client) => (
          <Select.Option key={client.id} value={client.id}>
            {client.name}
          </Select.Option>
        ))}
      </Select>
    </div>
  )
}

export const ToEmailsCell = ({
  value,
  row,
  onFieldChange,
}: {
  value: string
  row: ParsedRow
  onFieldChange: (rowNum: number, field: keyof ParsedRow, value: string) => void
}) => (
  <AntSelect
    mode="tags"
    size="small"
    tokenSeparators={[',', ' ', ';']}
    style={{ width: '100%', minWidth: 180 }}
    value={splitEmails(value)}
    open={false}
    onChange={(emails: string[]) =>
      onFieldChange(row.rowNum, 'toEmails', emails.join('; '))
    }
  />
)

export const PaymentTypeCell = ({
  value,
  row,
  error,
  onFieldChange,
}: {
  value: string
  row: ParsedRow
  error: string | undefined
  onFieldChange: (rowNum: number, field: keyof ParsedRow, value: string) => void
}) => {
  const isInvalidValue = isInvalidPaymentType(value)
  return (
    <div>
      {error ? (
        <FieldErrorMessage>
          <WarningOutlined />
          {isInvalidValue ? `"${value}": ${error}` : error}
        </FieldErrorMessage>
      ) : null}
      <AntSelect
        size="small"
        status={error ? 'error' : undefined}
        style={{ width: '100%' }}
        value={isInvalidValue || value === '' ? undefined : value}
        placeholder={isInvalidValue ? 'Select valid type' : '—'}
        allowClear
        onChange={(selected: string | undefined) =>
          onFieldChange(row.rowNum, 'paymentType', selected ?? '')
        }
      >
        {UPLOAD_PAYMENT_TYPE_LABELS.map((label) => (
          <AntSelect.Option key={label} value={label}>
            {label}
          </AntSelect.Option>
        ))}
      </AntSelect>
    </div>
  )
}

export const DueDayInputCell = ({
  value,
  row,
  error,
  onFieldChange,
}: {
  value: string
  row: ParsedRow
  error: string | undefined
  onFieldChange: (rowNum: number, field: keyof ParsedRow, value: string) => void
}) => (
  <div>
    {error ? (
      <FieldErrorMessage>
        <WarningOutlined />
        {error}
      </FieldErrorMessage>
    ) : null}
    <InvalidDateInput
      type="text"
      size={12}
      $invalid={Boolean(error)}
      value={value}
      placeholder="15"
      onChange={(e) => onFieldChange(row.rowNum, 'dueDay', e.target.value)}
    />
  </div>
)

export const RemoveRowCell = ({
  row,
  onRemoveRow,
}: {
  row: ParsedRow
  onRemoveRow: (rowNum: number) => void
}) => (
  <DeleteOutlined
    role="button"
    tabIndex={0}
    aria-label={`Remove row ${row.rowNum}`}
    style={{ color: sureco.danger, cursor: 'pointer', fontSize: 14 }}
    onClick={() => onRemoveRow(row.rowNum)}
    onKeyDown={(event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return
      event.preventDefault()
      onRemoveRow(row.rowNum)
    }}
  />
)
